import { i18n } from '../utils/i18n';

interface RoomConfig {
  max: number;
  min: number;
  adults: {
    min: number;
    max: number;
    default: number;
  };
  children: {
    min: number;
    max: number;
    default: number[];
    ageMin: number;
    ageMax: number;
  };
}

interface Room {
  adults: number;
  children: number;
  childAges: number[];
}

type ErrorType = 'maxAdults' | 'maxChildren';

class RoomModal {
  private readonly config: RoomConfig;
  private readonly rooms: Room[] = [];
  private modal: any = null;
  private container: HTMLElement | null = null;
  private confirmBtn: HTMLButtonElement | null = null;
  private confirmed = false;
  private roomsSnapshot: Room[] | null = null;
  private readonly onConfirm?: (rooms: Room[]) => void;
  private errors: ErrorType[] = [];

  constructor(config: RoomConfig, onConfirm?: (rooms: Room[]) => void) {
    this.config = config;
    this.onConfirm = onConfirm;
    this.rooms = [
      {
        adults: config.adults.default,
        children: config.children.default.length,
        childAges: [...config.children.default],
      },
    ];
  }

  public open(): void {
    // Save state snapshot so we can restore on cancel/dismiss
    this.confirmed = false;
    this.roomsSnapshot = this.rooms.map(r => ({
      ...r,
      childAges: [...r.childAges],
    }));

    const footer = this.createFooter();

    this.modal = new (window as any).Modal({
      title: i18n.t('modal.rooms.title'),
      className: 'passengers-room-title',
      content:
        '<div id="room-selector" class="container px-4 flex flex-col"></div>',
      footer: footer,
      trackingLabel: 'searchComponent-room',
      fullHeight: true,
      onClose: () => {
        if (!this.confirmed && this.roomsSnapshot) {
          this.rooms.length = 0;
          this.roomsSnapshot.forEach(r =>
            this.rooms.push({ ...r, childAges: [...r.childAges] })
          );
        }
        this.roomsSnapshot = null;
      },
    }).open();

    if (this.modal) {
      const title = document.querySelector(
        '.passengers-room-title .modal-title'
      ) as HTMLElement;
      if (title) {
        title.dataset.testid = 'passengers-search-header';
      }
    }

    this.container = document.getElementById('room-selector');
    if (this.container) {
      this.render();
    }
  }

  public close(): void {
    this.modal?.close();
  }

  public getRooms(): Room[] {
    return this.rooms;
  }

  public getTotalCounts(): { adults: number; children: number } {
    return this.rooms.reduce(
      (totals, room) => ({
        adults: totals.adults + room.adults,
        children: totals.children + room.children,
      }),
      { adults: 0, children: 0 }
    );
  }

  public reset(): void {
    this.rooms.length = 0;
    this.rooms.push({
      adults: this.config.adults.default,
      children: this.config.children.default.length,
      childAges: [...this.config.children.default],
    });
    this.errors = [];
    if (this.confirmBtn) {
      this.confirmBtn.disabled = false;
    }
    if (this.container) {
      this.render();
    }
  }

  private createFooter(): HTMLElement {
    const container = document.createElement('div');
    container.className = 'flex gap-3 justify-around';

    const clearBtn = document.createElement('button');
    clearBtn.className = 'btn btn-secondary btn-lg grow';
    clearBtn.textContent = i18n.t('modal.common.clearAll');
    clearBtn.dataset.action = 'clear';
    clearBtn.dataset.testid = 'search-modal-clearall';
    clearBtn.addEventListener('click', () => {
      this.confirmed = true;
      this.reset();
      this.onConfirm?.(this.getRooms());
    });

    this.confirmBtn = document.createElement('button');
    this.confirmBtn.className = 'btn btn-primary btn-lg grow';
    this.confirmBtn.textContent = i18n.t('modal.common.confirm');
    this.confirmBtn.dataset.action = 'confirm';
    this.confirmBtn.dataset.testid = 'search-modal-confirm';
    this.confirmBtn.addEventListener('click', () => {
      this.errors = this.validateTotals();

      if (this.errors.length > 0) {
        this.render();
        return;
      }
      this.confirmed = true;
      this.onConfirm?.(this.getRooms());
      this.close();
    });

    container.appendChild(clearBtn);
    container.appendChild(this.confirmBtn);
    return container;
  }

  private renderError(errorType: ErrorType): HTMLElement {
    const baseKey = errorType === 'maxAdults'
      ? 'modal.rooms.errorMaxAdults'
      : 'modal.rooms.errorMaxChildren';

    const errorDiv = document.createElement('label');
    errorDiv.className = 'text-error block mb-3';

    const textSpan = document.createElement('span');
    textSpan.textContent = i18n.t(baseKey) + ' ';
    errorDiv.appendChild(textSpan);

    const locale = i18n.getLocale().toLowerCase();
    const linkHref = errorType === 'maxAdults'
      ? `https://www.sundeals.com/${locale}/faq/before-you-book/help-with-booking#how-many-people-can-i-have-on-one-booking`
      : `https://www.sundeals.com/${locale}/faq/before-you-book/help-with-booking#how-many-children-can-i-have-on-one-booking`;

    const linkTextValue = i18n.t(`${baseKey}Link`);

    const link = document.createElement('a');
    link.className = 'form-link-secondary';
    link.href = linkHref;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';

    const words = linkTextValue.split(' ');
    const lastWord = words.pop();
    if (words.length > 0) {
      link.appendChild(document.createTextNode(words.join(' ') + ' '));
    }

    const nowrapSpan = document.createElement('span');
    nowrapSpan.style.whiteSpace = 'nowrap';
    nowrapSpan.textContent = lastWord + ' ';

    const chevron = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    chevron.setAttribute('class', 'icon icon-sm inline align-middle text-primary');
    chevron.setAttribute('aria-hidden', 'true');
    const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
    use.setAttribute('href', '#chevron-right');
    chevron.appendChild(use);
    nowrapSpan.appendChild(chevron);

    link.appendChild(nowrapSpan);
    errorDiv.appendChild(link);

    return errorDiv;
  }

  private render(): void {
    if (!this.container) return;

    const activeElement = document.activeElement as HTMLElement;
    const activeElementId = activeElement.id;
    const shouldRestoreFocus = this.container.contains(activeElement);

    this.container.innerHTML = '';

    this.errors.forEach(errorType => {
      this.container!.appendChild(this.renderError(errorType));
    });

    const numRoomsLabel = document.createElement('label');
    numRoomsLabel.textContent = i18n.t('modal.rooms.numberOfRooms');
    numRoomsLabel.className = 'text-primary font-bold mb-3';

    const numRoomsSelect = document.createElement('select');
    numRoomsSelect.className = 'w-40 form-select border-input';
    numRoomsSelect.id = `search-modal-rooms`;
    numRoomsSelect.dataset.testid = 'rooms-select';
    for (let i = this.config.min; i <= 3; i++) {
      const option = document.createElement('option');
      option.value = i.toString();
      option.dataset.testid = `rooms-select-${i}`;
      option.textContent = i.toString();
      option.selected = i === this.rooms.length;
      numRoomsSelect.appendChild(option);
    }

    numRoomsSelect.addEventListener('change', e => {
      const newCount = parseInt((e.target as HTMLSelectElement).value);
      while (this.rooms.length < newCount) {
        this.rooms.push({
          adults: this.config.adults.default,
          children: 0,
          childAges: [],
        });
      }
      while (this.rooms.length > newCount) {
        this.rooms.pop();
      }
      this.runValidation();
      this.render();
    });

    this.container.appendChild(numRoomsLabel);
    this.container.appendChild(numRoomsSelect);

    const separator = document.createElement('hr');
    separator.className = 'my-4 border-component';
    this.container.appendChild(separator);

    this.rooms.forEach((room, index) => {
      const roomCard = this.createRoomCard(room, index);
      this.container?.appendChild(roomCard);
    });

    if (shouldRestoreFocus) {
      const newElement = this.container.querySelector(
        `#${activeElementId}`
      ) as HTMLElement;
      newElement?.focus();
    }
  }

  private createRoomCard(room: Room, index: number): HTMLElement {
    const card = document.createElement('div');
    card.className =
      'border border-component rounded rounded-lg p-4 flex flex-col mb-4';

    const title = document.createElement('h4');
    title.textContent = `${i18n.t('modal.rooms.room')} ${index + 1}`;
    title.className = 'text-primary font-bold w-full';
    title.dataset.testid = `room-${index + 1}-title`;

    const separator = document.createElement('hr');
    separator.className = 'mt-1 mb-4 border-component';

    const row = document.createElement('div');
    row.className = 'grid grid-cols-2 grid-row-1 mb-4';

    const adultsCol = document.createElement('div');
    adultsCol.className = 'form-group';

    const adultsLabel = document.createElement('label');
    adultsLabel.textContent = i18n.t('modal.rooms.adults');
    adultsLabel.className = 'text-primary form-label';

    const adultsSelect = document.createElement('select');
    adultsSelect.id = `search-modal-adult-select-${index + 1}`;
    adultsSelect.className = 'form-select w-20 border-input';
    adultsSelect.dataset.testid = `search-adult-select-${index + 1}`;

    for (let i = this.config.adults.min; i <= this.config.adults.max; i++) {
      const option = document.createElement('option');
      option.value = i.toString();
      option.textContent = i.toString();
      option.selected = i === room.adults;
      adultsSelect.appendChild(option);
    }
    adultsSelect.addEventListener('change', e => {
      room.adults = parseInt((e.target as HTMLSelectElement).value);
      this.runValidation();
    });
    adultsCol.appendChild(adultsLabel);
    adultsCol.appendChild(adultsSelect);

    const childrenCol = document.createElement('div');
    childrenCol.className = 'form-group';

    const childrenLabel = document.createElement('label');
    childrenLabel.textContent = `${i18n.t('modal.rooms.children')} (${this.config.children.ageMin}-${this.config.children.ageMax})`;
    childrenLabel.className = 'text-primary form-label';
    const childrenSelect = document.createElement('select');
    childrenSelect.id = `search-modal-child-select-${index + 1}`;
    childrenSelect.className = 'form-select w-20 border-input';
    childrenSelect.dataset.testid = `search-child-select-${index + 1}`;

    for (let i = this.config.children.min; i <= this.config.children.max; i++) {
      const option = document.createElement('option');
      option.value = i.toString();
      option.textContent = i.toString();
      option.selected = i === room.children;
      childrenSelect.appendChild(option);
    }
    childrenSelect.addEventListener('change', e => {
      const newCount = parseInt((e.target as HTMLSelectElement).value);
      room.children = newCount;
      while (room.childAges.length < newCount) {
        room.childAges.push(this.config.children.ageMin);
      }
      while (room.childAges.length > newCount) {
        room.childAges.pop();
      }
      this.runValidation();
    });
    childrenCol.appendChild(childrenLabel);
    childrenCol.appendChild(childrenSelect);

    row.appendChild(adultsCol);
    row.appendChild(childrenCol);

    card.appendChild(title);
    card.appendChild(separator);
    card.appendChild(row);

    if (room.children > 0) {
      const childAgesSection = this.createChildAgesSection(room, index);
      card.appendChild(childAgesSection);
    }

    return card;
  }

  private createChildAgesSection(room: Room, roomIndex: number): HTMLElement {
    const section = document.createElement('div');

    const title = document.createElement('h4');
    title.textContent = i18n.t('modal.rooms.childAges');
    title.className = 'font-bold mb-2';

    const subtitle = document.createElement('p');
    subtitle.textContent = i18n
      .t('modal.rooms.childAgesSubtitle')
      .replace('{min}', this.config.children.ageMin.toString())
      .replace('{max}', this.config.children.ageMax.toString());
    subtitle.className = 'text-sm text-blue-600 mb-3';

    section.appendChild(title);
    section.appendChild(subtitle);

    const ageContainer = document.createElement('div');
    ageContainer.className = 'w-full grid grid-cols-1';

    room.childAges.forEach((age, index) => {
      const ageSelect = document.createElement('select');
      ageSelect.className = 'form-select w-48 border-input mb-3';
      ageSelect.id = `search-modal-age-select-${roomIndex + 1}-${index + 1}`;
      ageSelect.dataset.testid = `search-child-age-select-${roomIndex + 1}-${index + 1}`;

      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = '-';
      placeholder.disabled = true;
      placeholder.selected = age === null || age === undefined;
      ageSelect.appendChild(placeholder);

      for (
        let i = this.config.children.ageMin;
        i <= this.config.children.ageMax;
        i++
      ) {
        const option = document.createElement('option');
        option.value = i.toString();
        option.textContent =
          i === 0 ? i18n.t('modal.rooms.childAgeUnder1') : i.toString();
        option.selected = i === age;
        ageSelect.appendChild(option);
      }

      ageSelect.addEventListener('change', e => {
        room.childAges[index] = parseInt((e.target as HTMLSelectElement).value);
      });

      ageContainer.appendChild(ageSelect);
    });

    section.appendChild(ageContainer);

    return section;
  }

  public setRooms(
    rooms: Array<{ adults: number; children: number; childAges: number[] }>
  ): void {
    this.rooms.length = 0;
    rooms.forEach(room =>
      this.rooms.push({
        adults: Number(room.adults),
        children: Number(room.children),
        childAges: room.childAges.map(age => Number(age)),
      })
    );
    if (this.container) {
      this.render();
    }
  }

  private validateTotals(): ErrorType[] {
    const totals = this.getTotalCounts();
    const errors: ErrorType[] = [];
    if (totals.adults + totals.children > this.config.max) {
      errors.push('maxAdults');
    }
    if (totals.children > this.config.children.max) {
      errors.push('maxChildren');
    }
    return errors;
  }

  private runValidation(): void {
    this.errors = this.validateTotals();

    if (this.confirmBtn) {
      this.confirmBtn.disabled = this.errors.length > 0;
    }

    if (this.container) {
      this.render();
    }
  }
}

export default RoomModal;
